import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-paper px-6">
          <div className="text-center">
            <p className="font-display text-3xl mb-3">Something slipped off the press.</p>
            <p className="text-inksoft mb-6">Please reload the page — or call (917) 933-1693.</p>
            <button className="btn-ink" onClick={() => window.location.reload()} data-testid="error-reload-btn">
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LazyHome = React.lazy(() => import("@/pages/Home"));
const LazyServices = React.lazy(() => import("@/pages/Services"));
const LazyAbout = React.lazy(() => import("@/pages/About"));
const LazyQuote = React.lazy(() => import("@/pages/Quote"));
const LazyContact = React.lazy(() => import("@/pages/Contact"));

const LazyFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <span className="label-mono">Loading…</span>
  </div>
);

const Header = React.lazy(() => import("@/components/Header"));
const Footer = React.lazy(() => import("@/components/Footer"));

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(timer);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollManager />
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-ink focus:text-paper focus:px-4 focus:py-2 focus:rounded-sm"
        >
          Skip to main content
        </a>
        <React.Suspense fallback={<LazyFallback />}>
          <Header />
          <main id="main">
            <Routes>
              <Route path="/" element={<LazyHome />} />
              <Route path="/services" element={<LazyServices />} />
              <Route path="/about" element={<LazyAbout />} />
              <Route path="/quote" element={<LazyQuote />} />
              <Route path="/contact" element={<LazyContact />} />
              <Route
                path="*"
                element={
                  <div className="min-h-[70vh] flex flex-col items-center justify-center gap-6 px-6 text-center">
                    <Logo />
                    <p className="font-display text-4xl">That page never made it to print.</p>
                    <a href="/" className="btn-ink" data-testid="notfound-home-btn">
                      Back to home
                    </a>
                  </div>
                }
              />
            </Routes>
          </main>
          <Footer />
        </React.Suspense>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
